import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  getUserGameWallets,
  paymentService,
  type GameWalletItem,
} from '@/features/finance/services/paymentService';
import { dispatchBalanceRefresh } from '@/utils/balanceRefresh';
import { playClickSound } from '@/utils/soundUtils';
import {
  getDisplayIntegerDollars,
  getMaxGameWithdrawAmount,
  isRiverPayProviderCode,
} from '@/utils/amountStep';
import { getIconPathForProvider, hasIconForProvider } from '@/features/games/data/gameProviders';
import { Button, Skeleton, cx } from '@/components/ui';
import { useModalMotion } from '@/hooks/useModalMotion';
import { ANIMATION_REGISTRY } from '@/config/animationRegistry';
import {
  MODAL_BACKDROP_INNER_CLASS,
  MODAL_PANEL_CLASS,
  modalPanelStyle,
} from '@/styles/modalTokens';
import {
  wfSectionTitleStyle,
  wfMutedStyle,
  wfMdlLinkStyle,
  wfActDateStyle,
} from '@/styles/typography';

type Phase = 'edit' | 'processing' | 'done';

/** Matches fixed `--wd-withdraw-rows` list viewport. */
const WITHDRAW_SKELETON_ROWS = 7;

function WithdrawGameRowSkeleton() {
  return (
    <div className="wd-game-row" aria-hidden>
      <Skeleton className="wd-game-row__ico shrink-0 !rounded-[8px]" />
      <div className="wd-game-row__meta flex flex-col gap-1">
        <Skeleton className="h-3.5 w-[55%] max-w-[9rem] rounded-md" />
        <Skeleton className="h-3 w-[4.5rem] rounded-md" />
      </div>
      <Skeleton className="wd-game-row__input !rounded-[6px] !border-solid" />
      <Skeleton className="wd-game-row__max !rounded-[6px]" />
    </div>
  );
}

export interface GameWithdrawModalProps {
  open: boolean;
  onClose: () => void;
  focusProviderId?: string | null;
  onSuccess?: () => void;
}

function getBalance(item: GameWalletItem): number {
  const loose = item as unknown as {
    walletBalance?: number;
    balance?: { inGameBalance?: number } | number;
    inGameBalance?: number;
  };
  const raw =
    loose.walletBalance ??
    (typeof loose.balance === 'number'
      ? loose.balance
      : loose.balance?.inGameBalance) ??
    loose.inGameBalance ??
    0;
  return Number(raw) || 0;
}

function getWinningBalance(item: GameWalletItem): number {
  const loose = item as unknown as {
    winningBalance?: number;
    balance?: { winningBalance?: number };
  };
  const raw = loose.balance?.winningBalance ?? loose.winningBalance ?? 0;
  return Number(raw) || 0;
}

function getProviderId(item: GameWalletItem): string {
  return String(
    (item as { gameProviderId?: string }).gameProviderId ??
      item.game?.id ??
      (item as { gameProvider?: { id?: string } }).gameProvider?.id ??
      (item as { id?: string }).id ??
      ''
  );
}

function getName(item: GameWalletItem): string {
  return item.game?.name || (item as { gameProvider?: { name?: string } }).gameProvider?.name || 'Game';
}

function getCode(item: GameWalletItem): string | undefined {
  return item.game?.code || (item as { gameProvider?: { code?: string } }).gameProvider?.code;
}

function getWithdrawMax(item: GameWalletItem): number {
  return getMaxGameWithdrawAmount(getCode(item), getBalance(item), getWinningBalance(item));
}

function sanitizeAmountDraft(value: string): string | null {
  const sanitized = value.replace(/[^0-9.]/g, '');
  const parts = sanitized.split('.');
  if (parts.length > 2) return null;
  if (parts[1] && parts[1].length > 2) return null;
  return sanitized;
}

/**
 * Wireframe mWithdraw — `.ov > .mdl` width 460:
 * title · help · per-game rows (ico-s, amt inp, Max) · CTA / processing / done
 */
export default function GameWithdrawModal({
  open,
  onClose,
  focusProviderId,
  onSuccess,
}: GameWithdrawModalProps) {
  const [items, setItems] = useState<GameWalletItem[]>([]);
  const [loading, setLoading] = useState(false);
  /** True after the current open/focus fetch settles — avoids empty-state flash mid-load. */
  const [listReady, setListReady] = useState(false);
  const [amounts, setAmounts] = useState<Record<string, number>>({});
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<Phase>('edit');
  const [error, setError] = useState<string | null>(null);
  /** User dismissed while withdraw requests still in flight — ignore late UI updates. */
  const dismissedRef = useRef(false);

  const handleClose = () => {
    playClickSound();
    dismissedRef.current = true;
    onClose();
  };

  const { render, exiting, backdropClass, panelClass } = useModalMotion(open);

  /* Reset list UI before paint so stale rows never flash ahead of skeleton. */
  useLayoutEffect(() => {
    if (!open) return;
    dismissedRef.current = false;
    setPhase('edit');
    setError(null);
    setFocusedId(null);
    setDrafts({});
    setItems([]);
    setAmounts({});
    setListReady(false);
    // Keep list empty during enter — skeleton shimmer during fade causes open jank.
    setLoading(false);
  }, [open, focusProviderId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Defer fetch until enter motion finishes so skeleton/network don't fight the fade.
    const startId = window.setTimeout(() => {
      if (cancelled || dismissedRef.current) return;
      setLoading(true);
      getUserGameWallets()
        .then((res) => {
          if (cancelled || dismissedRef.current) return;
          const list =
            (res.data as { items?: GameWalletItem[] })?.items ||
            (res.data as { wallets?: GameWalletItem[] })?.wallets ||
            res.data ||
            [];
          const arr = Array.isArray(list) ? list : [];
          setItems(arr);
          const init: Record<string, number> = {};
          arr.forEach((item) => {
            const id = getProviderId(item);
            if (!id) return;
            // Focused game: prefill Max withdrawable (River Pay = winning only).
            init[id] =
              focusProviderId && id === focusProviderId ? getWithdrawMax(item) : 0;
          });
          setAmounts(init);
        })
        .catch(() => {
          if (!cancelled && !dismissedRef.current) setError('Failed to load game wallets');
        })
        .finally(() => {
          if (cancelled || dismissedRef.current) return;
          setListReady(true);
          setLoading(false);
        });
    }, ANIMATION_REGISTRY.modal.enterMs);
    return () => {
      cancelled = true;
      window.clearTimeout(startId);
    };
  }, [open, focusProviderId]);

  // Clear only after exit unmount — wiping mid-fade causes exit jank.
  useEffect(() => {
    if (open || render) return;
    setItems([]);
    setAmounts({});
    setListReady(false);
    setLoading(false);
    setPhase('edit');
    setError(null);
    setFocusedId(null);
    setDrafts({});
  }, [open, render]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        playClickSound();
        dismissedRef.current = true;
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const total = useMemo(
    () => Object.values(amounts).reduce((s, n) => s + (Number(n) || 0), 0),
    [amounts]
  );

  const setAmt = (id: string, value: number, max: number) => {
    setAmounts((prev) => ({ ...prev, [id]: Math.min(max, Math.max(0, value)) }));
  };

  const submit = async () => {
    playClickSound();
    const entries = Object.entries(amounts).filter(([, amt]) => amt > 0);
    if (!entries.length) {
      setError('Enter an amount for at least one game');
      return;
    }
    dismissedRef.current = false;
    setPhase('processing');
    setError(null);
    const errors: string[] = [];
    const successfulIds: string[] = [];
    for (const [gameProviderId, amount] of entries) {
      if (dismissedRef.current) return;
      try {
        const res = await paymentService.withdrawFromGame({
          gameProviderId,
          amount,
          description: 'Move from game to wallet',
        });
        if (res.success) {
          successfulIds.push(gameProviderId);
        } else {
          errors.push(res.message || gameProviderId);
        }
      } catch (e: unknown) {
        errors.push((e as { message?: string })?.message || gameProviderId);
      }
    }
    if (dismissedRef.current) return;

    // Refresh parent only when at least one withdraw landed (not on total failure).
    if (successfulIds.length > 0) {
      dispatchBalanceRefresh();
      onSuccess?.();
    }

    if (errors.length) {
      // Drop successful amounts so retry cannot double-withdraw those providers.
      if (successfulIds.length > 0) {
        setAmounts((prev) => {
          const next = { ...prev };
          for (const id of successfulIds) next[id] = 0;
          return next;
        });
        setDrafts((prev) => {
          const next = { ...prev };
          for (const id of successfulIds) delete next[id];
          return next;
        });
      }
      setError(errors.join('; '));
      setPhase('edit');
      return;
    }

    setPhase('done');
  };

  if (!render) return null;

  const fmt = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--wf-z-modal-nested)] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wd-mdl-title"
    >
      <div className={cx(MODAL_BACKDROP_INNER_CLASS, backdropClass)} aria-hidden />
      <div
        className={cx(MODAL_PANEL_CLASS, 'wd-withdraw-mdl', panelClass)}
        style={modalPanelStyle('--wf-mdl-withdraw-w')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wd-withdraw-mdl__head flex flex-col" style={{ gap: 'var(--wf-mdl-gap)' }}>
          <div className="flex items-center justify-between gap-2">
            <h2
              id="wd-mdl-title"
              className="font-display font-bold text-ink"
              style={wfSectionTitleStyle}
            >
              Withdraw to wallet
            </h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="inline-flex items-center justify-center w-8 h-8 rounded-control-sm border border-border-ice bg-white/5 text-ice hover:bg-white/10 hover:text-ink transition-colors focus-visible:outline-none focus-visible:shadow-focus-ring shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="font-ui text-caption-muted m-0" style={wfMutedStyle}>
            Move funds from a game back to your wallet. Tap Max for the full withdrawable
            amount (River Pay: win balance only), or type an amount.
          </p>

          {error && (
            <p className="font-ui text-danger-soft m-0" style={wfMdlLinkStyle}>
              {error}
            </p>
          )}
        </div>

        <div className="wd-withdraw-mdl__list cosmos-scroll-y">
          {loading &&
            Array.from({ length: WITHDRAW_SKELETON_ROWS }, (_, i) => (
              <WithdrawGameRowSkeleton key={`wd-skel-${i}`} />
            ))}

          {phase === 'processing' && (
            <div
              className="rounded-[8px] border border-border-ice bg-surface flex flex-col"
              style={{
                padding: 'calc(8px * var(--proto-scale))',
                gap: 'calc(6px * var(--proto-scale))',
                fontSize: 'var(--type-caption)',
              }}
            >
              <span className="font-ui text-ink">⏳ Moving funds — balance-sync check runs first…</span>
              <span className="font-ui text-caption-muted">
                Large amounts may need approval. You can close this — the transfer keeps running in the
                background.
              </span>
              <Button variant="secondary" size="sm" className="self-start mt-1" onClick={handleClose}>
                Close
              </Button>
            </div>
          )}

          {phase === 'done' && (
            <div
              className="rounded-[8px] border border-border-ice bg-surface flex flex-col"
              style={{
                padding: 'calc(8px * var(--proto-scale))',
                gap: 'calc(6px * var(--proto-scale))',
                fontSize: 'var(--type-caption)',
              }}
            >
              <span className="font-ui text-ink">✓ {fmt(total)} moved to your wallet</span>
              <Button variant="tertiary" size="sm" className="self-start" onClick={handleClose}>
                Done
              </Button>
            </div>
          )}

          {phase === 'edit' &&
            listReady &&
            !loading &&
            items.map((item) => {
              const id = getProviderId(item);
              const inGame = getBalance(item);
              const winning = getWinningBalance(item);
              const maxWithdraw = getWithdrawMax(item);
              const riverPay = isRiverPayProviderCode(getCode(item));
              // Keep River Pay visible when in-game > 0 even if win is $0 (user sees why Max is low).
              if (!id || (inGame <= 0 && maxWithdraw <= 0)) return null;
              const amt = amounts[id] || 0;
              const code = getCode(item);
              return (
                <div key={id} className="wd-game-row">
                  <span className="lobby-gi-ico wd-game-row__ico shrink-0 overflow-hidden rounded-[8px]">
                    {hasIconForProvider(code) ? (
                      <img
                        src={getIconPathForProvider(code || '')}
                        alt=""
                        className="block w-full h-full object-cover"
                        draggable={false}
                      />
                    ) : (
                      <span className="block w-full h-full bg-[linear-gradient(160deg,#1D3FA8_0%,#0A1B52_100%)]" />
                    )}
                  </span>
                  <div className="wd-game-row__meta flex flex-col min-w-0 gap-0">
                    <b className="font-ui font-bold text-ink truncate" style={wfMutedStyle}>
                      {getName(item)}
                    </b>
                    <span className="font-ui text-caption-muted" style={wfActDateStyle}>
                      {riverPay
                        ? `${fmt(inGame)} in game · Win ${fmt(getDisplayIntegerDollars(winning))} withdrawable`
                        : `${fmt(maxWithdraw)} available`}
                    </span>
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={
                      focusedId === id
                        ? (drafts[id] ?? '')
                        : amt > 0
                          ? `$${amt.toFixed(2)}`
                          : '$ —'
                    }
                    onFocus={() => {
                      setFocusedId(id);
                      setDrafts((prev) => ({
                        ...prev,
                        [id]: amt > 0 ? String(amt) : '',
                      }));
                    }}
                    onBlur={() => {
                      const raw = drafts[id] ?? '';
                      const n =
                        raw === '' || raw === '.'
                          ? 0
                          : parseFloat(raw);
                      setAmt(id, Number.isFinite(n) ? n : 0, maxWithdraw);
                      setFocusedId((cur) => (cur === id ? null : cur));
                      setDrafts((prev) => {
                        const next = { ...prev };
                        delete next[id];
                        return next;
                      });
                    }}
                    onChange={(e) => {
                      const next = sanitizeAmountDraft(e.target.value);
                      if (next === null) return;
                      setDrafts((prev) => ({ ...prev, [id]: next }));
                      if (next === '' || next === '.') {
                        setAmt(id, 0, maxWithdraw);
                        return;
                      }
                      const n = parseFloat(next);
                      if (Number.isFinite(n)) setAmt(id, n, maxWithdraw);
                    }}
                    className="wd-game-row__input"
                  />
                  <button
                    type="button"
                    className="wd-game-row__max"
                    disabled={maxWithdraw <= 0}
                    onClick={() => {
                      playClickSound();
                      setAmt(id, maxWithdraw, maxWithdraw);
                      if (focusedId === id) {
                        setDrafts((prev) => ({ ...prev, [id]: String(maxWithdraw) }));
                      }
                    }}
                  >
                    Max
                  </button>
                </div>
              );
            })}

          {phase === 'edit' &&
            listReady &&
            !loading &&
            items.every((i) => getWithdrawMax(i) <= 0 && getBalance(i) <= 0) && (
            <p className="font-ui text-caption-muted m-0" style={wfMutedStyle}>
              No in-game balances to withdraw.
            </p>
          )}
        </div>

        <div className="wd-withdraw-mdl__foot">
          {phase === 'edit' ? (
            <Button
              variant="moneyOut"
              size="lg"
              fullWidth
              disabled={total <= 0 || loading}
              onClick={submit}
            >
              Withdraw {total > 0 ? fmt(total) : ''} to wallet
            </Button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
